import styled, { css, keyframes } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { AnchoredOverlay } from "./AnchoredOverlay";
import { ButtonBase } from "./Button";
import type { DataTableResponsiveSize } from "./DataTable.types";
import {
  DataGrid,
  DataGridCell,
  DataGridHeaderCell,
  DataGridRow,
  DataGridRowGroup,
  TableHeaderIconButton,
} from "./Table";

export const DataTableRoot = styled(DataGrid)<{ $minHeight?: string }>`
  display: flex;
  min-height: ${({ $minHeight }) => $minHeight || "auto"};
  align-self: stretch;
  flex-direction: column;
  gap: 0;
  overflow: visible;
  border: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.surface};

  @media (min-width: 834px) {
    overflow: auto;
  }

  @media (min-width: 1680px) {
    margin: 0 auto;
  }
`;

export const DataTableHeader = styled(DataGridRowGroup)`
  display: flex;
  width: 100%;
  height: 56px;
  box-sizing: border-box;
  align-self: stretch;
  align-items: flex-start;
  border-bottom: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  background: ${vars.color.background.subtle};

  @media (min-width: 834px) and (max-width: 1200px) {
    position: sticky;
    top: 0;
    z-index: 30;
    min-width: 1169px;

    & > div:first-child {
      position: sticky;
      left: 0;
      z-index: 11;
      background: inherit;
      box-shadow: ${vars.shadow.frozenLeft};
    }
  }
`;

export const DataTableHeaderRow = styled.div`
  display: contents;
`;

export const DataTableScrollableCells = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const DataTableFixedCells = styled.div`
  display: flex;
  flex-shrink: 0;

  @media (min-width: 834px) and (max-width: 1200px) {
    position: sticky;
    right: 0;
    z-index: 10;
    background: inherit;
    box-shadow: ${vars.shadow.frozenRight};
    backface-visibility: hidden;
    will-change: transform;
  }
`;

export const DataTableHeaderCell = styled(DataGridHeaderCell).withConfig({
  shouldForwardProp: (prop) =>
    prop !== "cellWidth" && prop !== "flex" && prop !== "padding",
})<{
  cellWidth?: DataTableResponsiveSize;
  flex?: DataTableResponsiveSize;
  padding?: string;
}>`
  position: relative;
  display: flex;
  width: 100%;
  height: 56px;
  box-sizing: border-box;
  flex: 0 0 auto;
  align-self: stretch;
  align-items: center;
  gap: ${vars.spacing.xs};
  overflow: visible;
  padding: ${({ padding }) =>
    padding ||
    `${vars.spacing.px15} ${vars.spacing.lg} ${vars.spacing.lg} ${vars.spacing.sm}`};
  color: ${vars.color.action.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.sm};
  letter-spacing: 0;

  @media (min-width: 834px) {
    ${({ cellWidth }) =>
      cellWidth?.tablet && `width: ${cellWidth.tablet}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.tablet && `flex: ${flex.tablet}; width: auto;`}
  }

  @media (min-width: 1280px) {
    ${({ cellWidth }) =>
      cellWidth?.laptop && `width: ${cellWidth.laptop}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.laptop && `flex: ${flex.laptop}; width: auto;`}
  }

  @media (min-width: 1680px) {
    ${({ cellWidth }) =>
      cellWidth?.desktop && `width: ${cellWidth.desktop}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.desktop && `flex: ${flex.desktop}; width: auto;`}
  }
`;

export const DataTableFilterButton = styled(TableHeaderIconButton)``;

export const DataTableHeaderText = styled.div`
  display: flex;
  height: 25px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DataTableCell = styled(DataGridCell).withConfig({
  shouldForwardProp: (prop) =>
    prop !== "backgroundColor" &&
    prop !== "cellWidth" &&
    prop !== "flex" &&
    prop !== "padding",
})<{
  backgroundColor?: string;
  cellWidth?: DataTableResponsiveSize;
  flex?: DataTableResponsiveSize;
  padding?: string;
}>`
  display: flex;
  width: 100%;
  min-width: 0;
  height: 48px;
  box-sizing: border-box;
  flex: ${({ cellWidth }) => (cellWidth ? "0 0 auto" : "1")};
  flex-shrink: 1;
  align-self: stretch;
  align-items: center;
  overflow: hidden;
  padding: ${({ padding }) =>
    padding || `0 ${vars.spacing.md} 0 ${vars.spacing.px42}`};
  color: ${vars.color.action.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-style: normal;
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 834px) {
    ${({ cellWidth }) =>
      cellWidth?.tablet && `width: ${cellWidth.tablet}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.tablet && `flex: ${flex.tablet}; width: auto;`}
  }

  @media (min-width: 1280px) {
    ${({ cellWidth }) =>
      cellWidth?.laptop && `width: ${cellWidth.laptop}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.laptop && `flex: ${flex.laptop}; width: auto;`}
  }

  @media (min-width: 1680px) {
    ${({ cellWidth, flex }) => {
      if (cellWidth?.desktop) return `width: ${cellWidth.desktop};`;
      if (flex?.desktop) return `flex: ${flex.desktop};`;
      return "";
    }}
  }
`;

export const DataTableActionCell = styled(DataGridCell).withConfig({
  shouldForwardProp: (prop) => prop !== "cellWidth" && prop !== "flex",
})<{
  cellWidth?: DataTableResponsiveSize;
  flex?: DataTableResponsiveSize;
}>`
  display: flex;
  width: 100%;
  height: 48px;
  min-width: 0;
  flex: ${({ cellWidth }) => (cellWidth ? "0 0 auto" : "1")};
  flex-shrink: 1;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.sm};
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: ${vars.color.background.surface};
  }

  @media (min-width: 834px) {
    ${({ cellWidth }) =>
      cellWidth?.tablet && `width: ${cellWidth.tablet}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.tablet && `flex: ${flex.tablet}; width: auto;`}
  }

  @media (min-width: 1280px) {
    ${({ cellWidth }) =>
      cellWidth?.laptop && `width: ${cellWidth.laptop}; flex: 0 0 auto;`}
    ${({ flex }) => flex?.laptop && `flex: ${flex.laptop}; width: auto;`}
  }

  @media (min-width: 1680px) {
    ${({ cellWidth, flex }) => {
      if (cellWidth?.desktop) return `width: ${cellWidth.desktop};`;
      if (flex?.desktop) return `flex: ${flex.desktop};`;
      return "";
    }}
  }
`;

export const DataTableAction = styled(ButtonBase)`
  all: unset;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-family: ${vars.typography.family.sans};
  cursor: pointer;

  &:not(:disabled):hover {
    background: ${vars.color.action.primaryHover};
    color: ${vars.color.text.inverse};
  }

  &:disabled {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset ${vars.shadow.focus};
  }
`;

export const DataTableRow = styled(DataTableHeader).withConfig({
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
  width: 100%;
  height: 48px;
  align-items: center;
  border-bottom: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  background: ${vars.color.background.surface};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "default")};

  @media (min-width: 834px) and (max-width: 1200px) {
    & > div:first-child {
      position: sticky;
      left: 0;
      z-index: 20;
      background: ${vars.color.background.surface};
    }
  }

  &:hover {
    ${({ disabled }) =>
      disabled
        ? css`
            & > ${DataTableScrollableCells} > div,
            & > ${DataTableFixedCells} > div,
            & > div:first-child {
              background: ${vars.color.background.surface};
              cursor: not-allowed;
            }
          `
        : css`
            & > ${DataTableScrollableCells} > div,
            & > ${DataTableFixedCells} > div:not(${DataTableActionCell}) {
              background: ${vars.color.background.selection};
            }

            & > div:first-child {
              background: ${vars.color.background.selection};
            }
          `}
  }

  &:has(${DataTableAction}:hover) {
    & > ${DataTableScrollableCells} > div,
    & > ${DataTableFixedCells} > div:not(${DataTableActionCell}),
    & > div:first-child {
      background: ${vars.color.background.surface} !important;
    }
  }
`;

export const DataTableCellText = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
`;

export const DataTableVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: calc(-1 * ${vars.spacing.px1});
  overflow: hidden;
  padding: 0;
  border: 0;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

export const DataTableMenuTrigger = styled(ButtonBase).attrs({
  type: "button",
})`
  display: flex;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  background: ${vars.color.background.transparent};
  align-items: center;
  justify-content: center;
  color: ${vars.color.text.primary};
  cursor: default;

  ${DataTableAction} {
    cursor: default;
  }

  &:hover {
    background: ${vars.color.action.primaryPressed} !important;
    color: ${vars.color.text.inverse};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset ${vars.shadow.focus};
  }
`;

export const DataTableMenuAnchor = styled.div`
  position: relative;
`;

export const DataTableMenu = styled(AnchoredOverlay)`
  position: absolute;
  right: 0;
  z-index: 9999;
  width: 194px;
  height: 40px;
  flex-direction: column;
  align-items: flex-start;
  padding: ${vars.spacing.sm};
  border: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  border-radius: ${vars.radius.sm} 0 ${vars.radius.sm} ${vars.radius.sm};
  background: ${vars.color.background.canvas};
  color: ${vars.color.text.primary};
`;

export const DataTableDestructiveText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.px10};
  color: ${vars.color.text.danger};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-style: normal;
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.xs};
`;

export const DataTableMenuItem = styled(ButtonBase)`
  all: unset;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  cursor: default;

  &:hover {
    border-radius: ${vars.radius.sm};
    background: ${vars.color.action.danger};

    ${DataTableDestructiveText} {
      color: ${vars.color.text.inverse};
    }

    svg {
      color: ${vars.color.text.inverse};
      fill: ${vars.color.text.inverse};
    }
  }
`;

export const TableSearchRoot = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "disabled",
})<{
  disabled?: boolean;
  $focusVisible?: boolean;
}>`
  display: flex;
  width: 453px;
  height: 60px;
  box-sizing: border-box;
  align-items: center;
  overflow: hidden;
  margin-top: ${vars.spacing.px10};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.surface};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "default")};

  ${({ disabled, $focusVisible }) =>
    $focusVisible &&
    !disabled &&
    css`
      outline: none;
      box-shadow: ${vars.shadow.focus};
    `}

  &:hover {
    ${({ disabled }) =>
      disabled
        ? css`
            outline: none;
            background: ${vars.color.background.surface};
          `
        : css`
            outline: ${vars.borderWidth.medium} solid
              ${vars.color.action.primaryHover};
            background: ${vars.color.background.surface};
          `}
  }
`;

export const TableSearchField = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
  display: flex;
  height: 60px;
  box-sizing: border-box;
  flex: 1;
  flex-direction: column;
  padding: ${vars.spacing.xxs} 0 ${vars.spacing.xxs} ${vars.spacing.md};
  border: ${vars.borderWidth.thin} solid ${vars.color.control.border};
  border-right: 0;
  border-radius: ${vars.radius.sm} 0 0 ${vars.radius.sm};
  font-family: ${vars.typography.family.sans};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "default")};

  label,
  input {
    font-family: ${vars.typography.family.sans};
  }

  &:focus-within {
    border-color: ${({ disabled }) =>
      disabled ? vars.color.control.border : vars.color.action.primary};
  }
`;

export const TableSearchButton = styled(ButtonBase)`
  display: flex;
  width: 48px;
  height: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.px10};
  border: ${vars.borderWidth.thin} solid ${vars.color.action.primary};
  border-radius: 0 ${vars.radius.sm} ${vars.radius.sm} 0;
  background: ${vars.color.background.surface};
  color: ${vars.color.action.primary};

  &:hover {
    background: ${vars.color.action.primaryHover};
    color: ${vars.color.background.surface};
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled:hover {
    background: ${vars.color.background.surface};
    color: ${vars.color.action.primary};
  }
`;

export const TablePage = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
`;

export const TableToolbar = styled.div`
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: flex-start;
`;

export const CompactDataTableHeader = styled(DataGridRowGroup)`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-direction: row;
  border-bottom: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  background: ${vars.color.background.subtle};
`;

export const CompactDataTableRow = styled(CompactDataTableHeader)`
  background: ${vars.color.background.surface};

  &:hover {
    background: ${vars.color.background.selection};
  }
`;

export const CompactDataTableCell = styled(DataGridCell).withConfig({
  shouldForwardProp: (prop) => prop !== "backgroundColor" && prop !== "width",
})<{
  backgroundColor?: string;
  width?: string;
}>`
  display: flex;
  width: ${({ width }) => width || "100%"};
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  padding: ${vars.spacing.md} ${vars.spacing.xl};
  background: ${({ backgroundColor }) =>
    backgroundColor || vars.color.background.transparent};

  ${CompactDataTableHeader} &:hover {
    background: ${vars.color.action.primary};
    color: ${vars.color.text.inverse};

    svg [stroke]:not([stroke="none"]) {
      stroke: currentColor;
    }

    svg [fill]:not([fill="none"]) {
      fill: currentColor;
    }
  }
`;

export const DataTableBody = styled.div<{
  $preserveHeightWhileLoading?: boolean;
}>`
  position: relative;

  ${({ $preserveHeightWhileLoading }) =>
    $preserveHeightWhileLoading &&
    css`
      > :not([data-loading-row="true"]) {
        visibility: hidden;
      }

      > [data-loading-row="true"] {
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
      }
    `}
`;

const loadingSpinnerRotation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const DataTableLoadingRow = styled(DataGridRow).attrs({
  role: "row",
  "data-loading-row": "true",
})`
  min-height: 48px;
  align-items: center;
  border-bottom: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
`;

export const DataTableLoadingCell = styled(DataGridCell).attrs({
  role: "cell",
})`
  display: flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  color: ${vars.color.text.primary};
`;

export const DataTableLoadingStatus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.sm};
  font: inherit;
`;

export const DataTableLoadingSpinner = styled.span`
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  flex: 0 0 16px;
  border: ${vars.borderWidth.medium} solid currentColor;
  border-right-color: ${vars.color.border.transparent};
  border-radius: ${vars.radius.full};
  animation: ${loadingSpinnerRotation} ${vars.motion.processing} linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const DataTableMessage = styled.p`
  box-sizing: border-box;
  padding: 0 ${vars.spacing.md};
  color: inherit;
  font: inherit;
`;

export const DataTableEmptyMessage = styled(DataTableMessage)`
  text-align: center;
`;
